/* Sourced Society | ss-collection
   Filters drawer/sidebar, merged size chips, sort, Load more.
   Filtering and sorting update the page in place (Section Rendering API), no reload. */
(() => {
  if (customElements.get('ss-collection')) return;

  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const PREF_KEY = 'ss-col-side-hidden';
  const sizeKey = (v) => String(v).split('(')[0].replace(/🚀/g, '').replace(/!/g, '').replace(/\s+/g, '').replace(/\.EU/gi, 'EU').replace(/\.UK/gi, 'UK').toUpperCase();

  class SsCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-ss-filter-form]');
      this.toggleBtn = this.querySelector('[data-ss-filter-toggle]');

      try { if (localStorage.getItem(PREF_KEY) === '1') this.classList.add('is-side-hidden'); } catch (e) {}
      this.syncToggle();

      this.addEventListener('click', (e) => this.onClick(e));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeDrawer(); });
      DESKTOP.addEventListener ? DESKTOP.addEventListener('change', () => { this.closeDrawer(); this.syncToggle(); }) : null;

      if (this.form) {
        /* "Show results" just closes the drawer: results are already live */
        this.form.addEventListener('submit', (e) => { e.preventDefault(); this.closeDrawer(); });
        this.form.addEventListener('change', (e) => {
          if (e.target.matches('input[type="number"]')) return;
          this.apply();
        });
        /* Price fields: apply after typing stops */
        this.form.addEventListener('input', (e) => {
          if (!e.target.matches('input[type="number"]')) return;
          clearTimeout(this.priceTimer);
          this.priceTimer = setTimeout(() => this.apply(), 700);
        });
      }

      this.sort = this.querySelector('[data-ss-sort]');
      if (this.sort) this.sort.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.sort.value);
        url.searchParams.delete('page');
        this.render(url.toString());
      });

      window.addEventListener('popstate', () => this.render(window.location.href, false));

      this.dedupeSizeChips();
      this.sortSizes();
      this.trackHeader();
    }

    /* The theme header shrinks to the nav row while scrolling, so measure what is
       actually on screen and stick the filter sidebar/bar right under it. */
    trackHeader() {
      const hdr = document.querySelector('header.header') ||
        document.querySelector('.shopify-section-group-header-group[id$="__header"]');
      if (!hdr) return;
      const update = () => {
        this.raf = 0;
        const b = Math.max(0, Math.round(hdr.getBoundingClientRect().bottom));
        if (b !== this.headerBottom) {
          this.headerBottom = b;
          this.style.setProperty('--ss-header-h', b + 'px');
        }
      };
      const queue = () => { if (!this.raf) this.raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      if (window.ResizeObserver) new ResizeObserver(queue).observe(hdr);
      /* the header animates when it collapses, keep measuring briefly after scrolling stops */
      window.addEventListener('scroll', () => {
        clearTimeout(this.settle);
        this.settle = setTimeout(update, 350);
      }, { passive: true });
      update();
    }

    /* Put numeric size chips in number order (data arrives in variant order) */
    sortSizes() {
      this.querySelectorAll('.ss-col__sizes').forEach((wrap) => {
        const chips = [...wrap.children];
        if (!chips.every((c) => !isNaN(parseFloat(c.textContent)))) return;
        chips.sort((a, b) => parseFloat(a.textContent) - parseFloat(b.textContent)).forEach((c) => wrap.appendChild(c));
      });
    }

    syncToggle() {
      if (!this.toggleBtn) return;
      const open = DESKTOP.matches ? !this.classList.contains('is-side-hidden') : this.classList.contains('is-drawer-open');
      this.toggleBtn.setAttribute('aria-expanded', String(open));
    }

    openDrawer() {
      this.classList.add('is-drawer-open');
      document.documentElement.classList.add('ss-lock');
      this.syncToggle();
      const close = this.querySelector('.ss-col__side-close');
      if (close) close.focus({ preventScroll: true });
    }

    closeDrawer() {
      if (!this.classList.contains('is-drawer-open')) return;
      this.classList.remove('is-drawer-open');
      document.documentElement.classList.remove('ss-lock');
      this.syncToggle();
    }

    onClick(e) {
      const t = e.target;
      if (t.closest('[data-ss-filter-toggle]')) {
        if (DESKTOP.matches) {
          this.classList.toggle('is-side-hidden');
          try { localStorage.setItem(PREF_KEY, this.classList.contains('is-side-hidden') ? '1' : '0'); } catch (err) {}
          this.syncToggle();
        } else {
          this.openDrawer();
        }
        return;
      }
      if (t.closest('[data-ss-filter-close]')) { this.closeDrawer(); return; }

      const opener = t.closest('[data-ss-open]');
      if (opener) {
        const d = document.getElementById(opener.dataset.ssOpen);
        if (d && d.showModal) {
          d.showModal();
          document.documentElement.classList.add('ss-lock');
          if (!d.dataset.bound) {
            d.dataset.bound = '1';
            d.addEventListener('click', (ev) => { if (ev.target === d || ev.target.closest('[data-ss-close]')) d.close(); });
            d.addEventListener('close', () => document.documentElement.classList.remove('ss-lock'));
          }
        }
        return;
      }

      const chip = t.closest('[data-ss-chip]');
      if (chip && /option\.size/i.test(chip.dataset.ssChip)) {
        e.preventDefault();
        const param = chip.dataset.ssChip;
        const key = sizeKey(chip.dataset.value);
        const url = new URL(window.location.href);
        const keep = url.searchParams.getAll(param).filter((v) => sizeKey(v) !== key);
        url.searchParams.delete(param);
        keep.forEach((v) => url.searchParams.append(param, v));
        url.searchParams.delete('page');
        e.stopPropagation();
        this.render(url.toString());
        return;
      }

      const link = t.closest('.ss-col__active a, .ss-col__side-foot a, .ss-col__none a');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        this.render(link.href);
        return;
      }

      const more = t.closest('[data-ss-loadmore]');
      if (more) { e.preventDefault(); e.stopPropagation(); this.loadMore(more); }
    }

    apply() {
      const url = new URL(this.form.getAttribute('action'), window.location.origin);
      const data = new FormData(this.form);
      for (const [k, v] of data.entries()) {
        if (String(v).trim() !== '') url.searchParams.append(k, v);
      }
      if (this.sort) url.searchParams.set('sort_by', this.sort.value);
      this.form.querySelectorAll('[data-ss-group]:checked').forEach((el) => {
        el.dataset.values.split('||').forEach((v) => url.searchParams.append(el.dataset.ssGroup, v));
      });
      this.render(url.toString());
    }

    async render(href, push = true) {
      const ticket = (this.ticket = (this.ticket || 0) + 1);
      this.classList.add('is-loading');
      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.delete('section_id');
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.sectionId);
        const res = await fetch(fetchUrl.toString());
        const html = await res.text();
        if (ticket !== this.ticket) return; /* a newer filter change won */
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('ss-collection');
        if (!fresh) throw new Error('no section');

        const swap = (sel) => {
          const a = this.querySelector(sel);
          const b = fresh.querySelector(sel);
          if (a && b) a.innerHTML = b.innerHTML;
        };
        swap('.ss-col__main');
        swap('.ss-col__total');
        swap('[data-ss-count]');
        swap('[data-ss-filter-toggle]');

        /* Refresh filter counts but keep which groups are open and the scroll position */
        const body = this.querySelector('.ss-col__side-body');
        const freshBody = fresh.querySelector('.ss-col__side-body');
        if (body && freshBody) {
          const side = this.querySelector('.ss-col__side');
          const sideTop = side ? side.scrollTop : 0;
          const bodyTop = body.scrollTop;
          const open = [...body.querySelectorAll('details')].map((d) => d.open);
          body.innerHTML = freshBody.innerHTML;
          body.querySelectorAll('details').forEach((d, i) => { if (open[i] !== undefined) d.open = open[i]; });
          if (side) side.scrollTop = sideTop;
          body.scrollTop = bodyTop;
        }
        const sortInput = this.form && this.form.querySelector('input[name="sort_by"]');
        if (sortInput && this.sort) sortInput.value = this.sort.value;

        this.dedupeSizeChips();
        this.sortSizes();
        if (push) history.pushState({ ssCol: true }, '', url.pathname + url.search);

        /* If the grid top is off screen (e.g. after removing a chip lower down), bring it into view on desktop */
        if (DESKTOP.matches) {
          const top = this.querySelector('.ss-col__layout').getBoundingClientRect().top;
          if (top < 0) window.scrollBy({ top: top - 80, behavior: 'smooth' });
        }
      } catch (err) {
        window.location.href = href;
      } finally {
        if (ticket === this.ticket) this.classList.remove('is-loading');
      }
    }

    /* "36EU" and "36 EU" are separate values in Shopify; show one chip each */
    dedupeSizeChips() {
      const seen = new Set();
      this.querySelectorAll('[data-ss-chip]').forEach((chip) => {
        if (!/option\.size/i.test(chip.dataset.ssChip)) return;
        const key = sizeKey(chip.dataset.value);
        if (seen.has(key)) { chip.remove(); return; }
        seen.add(key);
        const k = key.replace(/(UK|EU)$/, '');
        if (/UK$/.test(key)) chip.firstChild.textContent = 'UK ' + k + ' ';
        else if (/EU$/.test(key)) chip.firstChild.textContent = 'EU ' + k + ' ';
      });
    }

    async loadMore(btn) {
      if (btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      try {
        const url = new URL(btn.dataset.href, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const res = await fetch(url.toString());
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = this.querySelector('[data-ss-grid]');
        const items = doc.querySelectorAll('[data-ss-grid] > li');
        items.forEach((li) => { li.classList.add('is-new'); grid.appendChild(li); });

        const total = Number(btn.dataset.total) || 0;
        const shown = grid.children.length;
        const shownEl = this.querySelector('[data-ss-shown]');
        const bar = this.querySelector('[data-ss-bar]');
        if (shownEl) shownEl.textContent = shown;
        if (bar && total) bar.style.width = Math.min(100, (shown / total) * 100) + '%';

        const next = doc.querySelector('[data-ss-loadmore]');
        if (next) {
          btn.dataset.href = next.dataset.href;
          btn.classList.remove('is-loading');
        } else {
          btn.remove();
        }
      } catch (err) {
        window.location.href = btn.dataset.href;
      }
    }
  }

  customElements.define('ss-collection', SsCollection);
})();
